import { DragEvent, ReactNode, useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';

import { AGENT_DURATIONS, AGENT_EASING } from '../../../Helpers/Agents/animations';
import { ATTACHMENT_HINT } from '../../../Helpers/Agents/attachments';

interface AttachmentDropZoneProps {
  enabled: boolean;
  onDrop: (files: FileList) => void;
  className?: string;
  children: ReactNode;
}

const carriesFiles = (event: DragEvent) => Array.from(event.dataTransfer.types).includes('Files');

/** Takes files dropped anywhere on the conversation, with a soft overlay while a drag is over it. */
const AttachmentDropZone = ({ enabled, onDrop, className = '', children }: AttachmentDropZoneProps) => {
  const { t } = useTranslation();
  const [isOver, setIsOver] = useState(false);
  // Child elements fire their own enter/leave events; the depth keeps the overlay steady.
  const depth = useRef(0);

  const reset = () => {
    depth.current = 0;
    setIsOver(false);
  };

  // Every file drag is handled, so a drop never opens the file in the tab; only `enabled` takes the files.
  const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
    if (!carriesFiles(event)) return;
    event.preventDefault();
    depth.current += 1;
    setIsOver(true);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!carriesFiles(event)) return;
    event.preventDefault();
    // eslint-disable-next-line no-param-reassign
    event.dataTransfer.dropEffect = enabled ? 'copy' : 'none';
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (!carriesFiles(event)) return;
    depth.current -= 1;
    if (depth.current <= 0) reset();
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    if (!carriesFiles(event)) return;
    event.preventDefault();
    reset();
    if (enabled) onDrop(event.dataTransfer.files);
  };

  return (
    <div
      className={`relative ${className}`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}>
      {children}
      <AnimatePresence>
        {enabled && isOver && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: AGENT_DURATIONS.fast, ease: AGENT_EASING.outExpo }}
            className="pointer-events-none absolute inset-2 z-20 flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed"
            style={{ borderColor: 'rgba(124, 108, 246, 0.45)', backgroundColor: 'rgba(251, 250, 249, 0.94)' }}>
            <span
              className="flex h-11 w-11 items-center justify-center rounded-full"
              style={{ backgroundColor: 'rgba(124, 108, 246, 0.1)', color: '#7c6cf6' }}>
              <Upload size={20} strokeWidth={1.75} />
            </span>
            <p className="m-0 text-sm font-medium text-gray-900">{t('Agent.DropFilesHere')}</p>
            <p className="m-0 text-xs text-gray-500">{ATTACHMENT_HINT}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AttachmentDropZone;
